import { PromptInputButton } from '@crowdex/ui/ai/prompt-input';
import { cn } from '@crowdex/ui/lib/utils';
import { Plus } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useEffectEvent,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useComposerMenuSide } from '@/components/desktop-shell/composer-menu-side';
import { MobileSheetHeader } from '@/components/mobile-shell/mobile-sheet-header';
import { useMediaQuery } from '@/hooks/use-media-query';
import type { AcpSelectConfigOption } from '@/lib/desktop-config-options';
import { COMPOSER_COMMANDS } from '@/lib/desktop-shell/composer-commands';
import type { PickerAction } from '@/lib/desktop-shell/composer-picker';
import type { RepositoryFile } from '@/lib/desktop-shell/repository-files';
import { useRepositoryFiles } from '@/lib/desktop-shell/use-repository-files';
import type { AvailablePluginSkill } from '@/lib/native-agent/types';
import { matchesShortcut } from '@/lib/platform';
import { ATTACH_ARTIFACT_SHORTCUT, currentShortcut } from '@/lib/settings-page/keyboard-shortcuts';
import type { MentionTarget } from '@/lib/side-panel/mention-targets';
import { ComposerMenuPanel } from './composer-menu-list';
import { ComposerPicker, type ComposerPlusSettings, type PickerOpenScope } from './composer-picker';

/** The + opened from elsewhere: Backspace out of / or @. */
export type ComposerPlusMenuHandle = { open: () => void };

/** The height the + menu needs above the composer, else it opens below. */
const PLUS_MENU_ROOM_PX = 440;

const DESKTOP_ACTIONS: readonly PickerAction[] = ['files', 'screenshot', 'mention-file'];
const PHONE_ACTIONS: readonly PickerAction[] = ['camera', 'photos', 'files', 'mention-file'];

/**
 * The composer's + (D7): the composer's one picker, unscoped. Without a query, what to add
 * (files, a screenshot, a file of the repository), the recent skills and the settings (the
 * agent, Plan mode); what is typed searches everything. / and @ are the same picker, scoped
 * (`onOpenScope`). On a phone, a sheet. ⌘⇧A opens @ on the artifacts.
 */
export const ComposerPlusMenu = forwardRef<
  ComposerPlusMenuHandle,
  {
    targets: readonly MentionTarget[];
    disabled: boolean;
    insertArtifact: (target: MentionTarget) => void;
    insertMention: (target: MentionTarget) => void;
    insertSkill: (skill: AvailablePluginSkill) => void;
    /** Writes files of the repository as the agents read them (`@path`). */
    insertFiles?: ((files: readonly RepositoryFile[]) => void) | undefined;
    /** The conversation's settings (the model and its permissions among them). */
    modelOptions: readonly AcpSelectConfigOption[];
    onChangeModel: (configId: string, value: string) => void;
    /** Opens / or @ in the composer (Mention a file, All skills, ⌘⇧A). */
    onOpenScope?: ((scope: PickerOpenScope) => void) | undefined;
    /** Whether its search finds the repository's files (and in the preview's). */
    repositoryFiles?: { preview: boolean } | undefined;
    settings?: ComposerPlusSettings;
    skills: readonly AvailablePluginSkill[];
  }
>(function ComposerPlusMenu(
  {
    targets,
    disabled,
    insertArtifact,
    insertMention,
    insertSkill,
    insertFiles,
    modelOptions,
    onChangeModel,
    onOpenScope,
    repositoryFiles,
    settings = {},
    skills,
  },
  ref,
) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [composer, setComposer] = useState<Element | null>(null);
  // Above the composer, as in a conversation; below it when there is no room above (the start
  // screen's composer sits near the top of the pane).
  const [placement, setPlacement] = useState<'above' | 'below'>('above');
  const touchScreen = useMediaQuery('(hover: none) and (pointer: coarse)');
  // The start screen's menus open down, the +'s with them.
  const menuSide = useComposerMenuSide();
  const [query, setQuery] = useState('');
  const files = useRepositoryFiles(
    open && repositoryFiles !== undefined && query.trim().length > 0 ? query.trim() : null,
    repositoryFiles?.preview ?? false,
  );

  const close = useCallback((): void => {
    // The focus was in the menu, which goes: it returns to the + that opened it (a choice that
    // focuses something else, the editor or a file dialog, takes it from there afterwards).
    const active = document.activeElement;
    if (active === null || active === document.body || panelRef.current?.contains(active))
      triggerRef.current?.focus();
    setOpen(false);
    setQuery('');
  }, []);

  function openMenu(): void {
    const element = triggerRef.current?.closest('.desktop-composer') ?? null;
    setComposer(element);
    setPlacement(
      menuSide === 'bottom' ||
        (element !== null && element.getBoundingClientRect().top < PLUS_MENU_ROOM_PX)
        ? 'below'
        : 'above',
    );
    setQuery('');
    setOpen(true);
  }

  useImperativeHandle(ref, () => ({ open: openMenu }));

  function openScope(scope: PickerOpenScope): void {
    setOpen(false);
    setQuery('');
    onOpenScope?.(scope);
  }

  // The latest onOpenScope, without listening again when it changes.
  const openArtifacts = useEffectEvent(() => openScope('artifacts'));
  useEffect(() => {
    if (disabled) return;
    function onShortcut(event: KeyboardEvent): void {
      const keys = currentShortcut('attach-artifact') ?? ATTACH_ARTIFACT_SHORTCUT;
      if (!matchesShortcut(event, keys)) return;
      const active = document.activeElement;
      const composerElement = triggerRef.current?.closest('.desktop-composer');
      if (!composerElement || !(active instanceof Node) || !composerElement.contains(active))
        return;
      event.preventDefault();
      openArtifacts();
    }
    document.addEventListener('keydown', onShortcut);
    return () => document.removeEventListener('keydown', onShortcut);
  }, [disabled]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent): void {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      if (target.closest('[data-slot="dropdown-menu-content"]')) return;
      close();
    }
    // Esc from anywhere but its field (which clears its query first) and its submenus (which
    // close themselves): the phone's back sends one too.
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const target = event.target;
      if (target instanceof Element && target.closest('[data-slot="dropdown-menu-content"]'))
        return;
      event.preventDefault();
      close();
    }
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [close, open]);

  const panel =
    open && composer !== null
      ? createPortal(
          <ComposerMenuPanel
            bodyClassName="flex max-h-none min-h-0 flex-col p-0"
            className={cn(
              'flex flex-col',
              !touchScreen && 'right-auto w-[21.25rem] max-w-[calc(100vw-2rem)]',
            )}
            header={
              // A phone's sheet (mobile 5) has its title.
              touchScreen ? (
                <div className="px-1">
                  <MobileSheetHeader closeLabel="Close" onClose={close} title="Add context" />
                </div>
              ) : undefined
            }
            label="Add context"
            menuRef={panelRef}
            placement={touchScreen ? 'above' : placement}
            role="dialog"
            sheet
          >
            <ComposerPicker
              commands={COMPOSER_COMMANDS}
              actions={(touchScreen ? PHONE_ACTIONS : DESKTOP_ACTIONS).filter(
                (action) => action !== 'mention-file' || onOpenScope !== undefined,
              )}
              files={files}
              modelOptions={modelOptions}
              onChangeModel={onChangeModel}
              onDismiss={close}
              onMention={insertMention}
              onOpenScope={onOpenScope === undefined ? undefined : openScope}
              onPicked={(item) => {
                // Plan mode toggles in place; a submenu's choice closes it with the +.
                if (item.type === 'setting' && item.setting === 'plan') return;
                close();
              }}
              onReferences={({ files: chosenFiles, targets: chosen }) => {
                if (chosenFiles.length > 0) insertFiles?.(chosenFiles);
                for (const target of chosen) insertArtifact(target);
              }}
              onSkill={insertSkill}
              query={query}
              scope="all"
              settings={settings}
              skills={skills}
              targets={targets}
              touch={touchScreen}
              typeAhead={{ onChange: setQuery }}
            />
          </ComposerMenuPanel>,
          // A touch screen's sheet is the screen's, above the composer that would clip it.
          touchScreen ? document.body : composer,
        )
      : null;

  return (
    <>
      <PromptInputButton
        aria-expanded={open}
        aria-label="Add context"
        className="size-8 rounded-control bg-control text-foreground hover:bg-control/80"
        disabled={disabled}
        onClick={() => {
          if (open) {
            close();
            return;
          }
          openMenu();
        }}
        ref={triggerRef}
      >
        <Plus className="size-4" />
      </PromptInputButton>
      {panel}
    </>
  );
});
